<script setup>
import { computed } from 'vue'
import { Link, usePage } from '@inertiajs/vue3'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
import teacher from '@/routes/teacher'

const page = usePage()

const tabs = [
  { name: 'Personal Information', href: teacher.settings.personalInfo(), icon: 'person' },
  { name: 'About Me', href: teacher.settings.aboutMe(), icon: 'edit_note' },
  { name: 'Subjects & Expertise', href: teacher.settings.subjects(), icon: 'school' },
  { name: 'Availability & Pricing', href: teacher.settings.availability(), icon: 'event_available' },
  { name: 'Bank Details', href: teacher.settings.bankDetails(), icon: 'account_balance' },
  { name: 'Notifications', href: teacher.settings.notifications(), icon: 'notifications' },
]

const activeTab = computed(() => {
  const currentPath = page.url
  return tabs.find(tab => currentPath.startsWith(tab.href)) || tabs[0]
})
</script>

<template>
  <DashboardLayout>
    <div class="space-y-6">
      <!-- Header -->
      <div class="border-b border-gray-200 pb-6">
        <h1 class="text-3xl font-bold text-gray-900">Tutor Settings</h1>
        <p class="text-gray-600 mt-2">Manage your profile, subjects, availability, and payment information</p>
      </div>

      <!-- Tab Navigation -->
      <div class="border-b border-gray-200">
        <nav class="-mb-px flex space-x-8 overflow-x-auto pb-px" aria-label="Tabs">
          <Link
            v-for="tab in tabs"
            :key="tab.name"
            :href="tab.href"
            :class="[
              activeTab.href === tab.href
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
              'group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors'
            ]"
          >
            <span
              :class="[
                activeTab.href === tab.href ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-500',
                'material-symbols-outlined mr-2 text-xl'
              ]"
            >
              {{ tab.icon }}
            </span>
            {{ tab.name }}
          </Link>
        </nav>
      </div>

      <!-- Welcome Message -->
      <div class="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100">
        <div class="flex items-start gap-4">
          <div class="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center shrink-0">
            <span class="material-symbols-outlined text-white text-2xl">info</span>
          </div>
          <div class="flex-1">
            <h2 class="text-xl font-bold text-gray-900 mb-2">Welcome to Your Settings</h2>
            <p class="text-gray-700 leading-relaxed">
              Use the tabs above to navigate through different sections of your tutor profile.
              Keep your information up to date to attract more students and provide the best learning experience.
            </p>
            <div class="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div class="flex items-start gap-2">
                <span class="material-symbols-outlined text-blue-600 mt-0.5">check_circle</span>
                <span class="text-sm text-gray-700">Update your personal information and bio</span>
              </div>
              <div class="flex items-start gap-2">
                <span class="material-symbols-outlined text-blue-600 mt-0.5">check_circle</span>
                <span class="text-sm text-gray-700">Manage subjects and teaching expertise</span>
              </div>
              <div class="flex items-start gap-2">
                <span class="material-symbols-outlined text-blue-600 mt-0.5">check_circle</span>
                <span class="text-sm text-gray-700">Set your availability and pricing</span>
              </div>
              <div class="flex items-start gap-2">
                <span class="material-symbols-outlined text-blue-600 mt-0.5">check_circle</span>
                <span class="text-sm text-gray-700">Configure payment and bank details</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </DashboardLayout>
</template>
